import React, { useState, useEffect, useRef } from "react";
import { Dialog, InputBase } from "@mui/material";
import Icon from "@mui/material/Icon";
import axios from "axios";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import StatusChip from "./StatusChip";
import PlanBadge from "./PlanBadge";

function GlobalSearch({ open, onClose, onSelect }) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [allTenants, setAllTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Fetch tenants when dialog opens
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIndex(0);
      fetchTenants();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_CONSOLE_API}/console/v1/getCustomers`,
        {
          headers: {
            "x-api-key": process.env.REACT_APP_CONSOLE_API_KEY,
            ...(token && { Authorization: token }),
          },
        }
      );
      setAllTenants(response.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch tenants:", err);
      setAllTenants([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter tenants based on query
  const results = query.trim()
    ? allTenants.filter(
        (t) =>
          (t.legalEntityName || "").toLowerCase().includes(query.toLowerCase()) ||
          (t.name || "").toLowerCase().includes(query.toLowerCase()) ||
          (t.companyName || "").toLowerCase().includes(query.toLowerCase()) ||
          (t.contactEmail || "").toLowerCase().includes(query.toLowerCase()) ||
          (t._id || "").toLowerCase().includes(query.toLowerCase())
      )
    : allTenants;

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && results[selectedIndex]) {
      onSelect(results[selectedIndex]);
      onClose();
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          margin: 0,
        },
      }}
    >
      {/* Search Input */}
      <MDBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2.5,
          py: 2,
          borderBottom: "1px solid #E8EDF2",
        }}
      >
        <Icon sx={{ color: "#5D6D7E", fontSize: "22px !important" }}>search</Icon>
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
          onKeyDown={handleKeyDown}
          placeholder="Search tenants by name, ID, or email…"
          sx={{ flex: 1, fontSize: 15 }}
          autoFocus
        />
        <MDBox
          sx={{
            background: "#F2F3F4",
            borderRadius: "6px",
            px: 1,
            py: 0.3,
            fontSize: 11,
            color: "#5D6D7E",
            fontWeight: 600,
          }}
        >
          ESC
        </MDBox>
      </MDBox>

      {/* Results */}
      <MDBox sx={{ maxHeight: 400, overflow: "auto", minHeight: 100 }}>
        {loading ? (
          <MDBox py={4} textAlign="center">
            <MDTypography variant="body2" color="secondary">
              Loading tenants…
            </MDTypography>
          </MDBox>
        ) : results.length === 0 ? (
          <MDBox py={4} textAlign="center">
            <MDTypography variant="body2" color="secondary">
              {query.trim() ? `No results found for "${query}"` : "No tenants found"}
            </MDTypography>
          </MDBox>
        ) : (
          <>
            <MDBox px={2.5} py={1}>
              <MDTypography sx={{ fontSize: 11, color: "#5D6D7E", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {query.trim() ? `Results (${results.length})` : `All Tenants (${results.length})`}
              </MDTypography>
            </MDBox>
            {results.map((item, i) => (
              <MDBox
                key={item._id || i}
                onClick={() => { onSelect(item); onClose(); }}
                sx={{
                  px: 2.5,
                  py: 1.5,
                  cursor: "pointer",
                  background: i === selectedIndex ? "#D4E6F1" : "transparent",
                  "&:hover": { background: "#F8FAFC" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #F0F3F6",
                }}
              >
                <MDBox>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDTypography sx={{ fontSize: 14, fontWeight: 600, color: "#1E3A5F" }}>
                      {item.legalEntityName || item.companyName || item.name || "Unnamed"}
                    </MDTypography>
                    <span style={{ fontFamily: "monospace", fontSize: 11, color: "#00ACC4" }}>
                      {item._id || item.tenantId || ""}
                    </span>
                    {(item._id || item.tenantId) && (
                      <span
                        onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(item._id || item.tenantId); }}
                        className="material-icons-outlined"
                        style={{ cursor: "pointer", color: "#BDC3C7", fontSize: 12, marginLeft: 4, verticalAlign: "middle" }}
                        title="Copy ID"
                      >
                        content_copy
                      </span>
                    )}
                  </MDBox>
                  <MDTypography sx={{ fontSize: 12, color: "#5D6D7E", mt: 0.3 }}>
                    {item.contactEmail || item.email || ""}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" gap={1} alignItems="center">
                  {(item.status || item.customerStatus) && (
                    <StatusChip status={(item.status || item.customerStatus || "").charAt(0).toUpperCase() + (item.status || item.customerStatus || "").slice(1)} />
                  )}
                  {(item.plan || item.subscriptionPlan) && (
                    <PlanBadge plan={item.plan || item.subscriptionPlan} />
                  )}
                </MDBox>
              </MDBox>
            ))}
          </>
        )}
      </MDBox>

      {/* Footer */}
      <MDBox
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: "1px solid #E8EDF2",
          background: "#F8FAFC",
          display: "flex",
          gap: 3,
        }}
      >
        <MDTypography sx={{ fontSize: 11, color: "#5D6D7E" }}>
          <strong>↑↓</strong> Navigate
        </MDTypography>
        <MDTypography sx={{ fontSize: 11, color: "#5D6D7E" }}>
          <strong>↵</strong> Select
        </MDTypography>
        <MDTypography sx={{ fontSize: 11, color: "#5D6D7E" }}>
          <strong>esc</strong> Close
        </MDTypography>
      </MDBox>
    </Dialog>
  );
}

export default GlobalSearch;
